import Ten99RouteConstants from 'Agent/1099s/Ten99RouteConstants';
import AccountRouteConstants from 'Agent/Accounts/AccountRouteConstants';
import {AgentInformationRouteConstants} from 'Agent/AgentInformation/AgentInformationRouteConstants';
import CommissionRouteConstants from 'Agent/Commissions/CommissionsRouteConstants';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import DashboardCard from 'Core/components/Dashboard/Card';
import 'Core/components/Dashboard/Dashboard.sass';
import {showModal} from 'Core/ducks/modal';
import * as O from 'fp-ts/lib/Option';
import React from 'react';
import {connect, useSelector} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import RequireScope from 'Shared/components/RequireScope';
import withUserClaims, {WithClaimsProps} from 'Shared/components/withUserClaims';
import {AgentNumberStatusId} from 'Shared/constants/AgentNumberStatus';
import {Scope} from 'Shared/constants/Scope';
import {UserRouteConstants} from 'Users/Routes';
import {isAgencyInStatus, selectUserScopes} from 'Users/selectors/userProfileSelectors';
import AgentNewsCard from './AgentNewsCard';
import {ExclusivityStatus} from 'Users/models/User/ExclusivityStatus';

type DashboardProps = {
  showModal: typeof showModal;
} & ReturnType<typeof mapStateToProps> &
  WithClaimsProps &
  RouteComponentProps;

export const Dashboard = (props: DashboardProps) => {
  const {selectedAgency} = useSelector((state: ReducerState) => state.user.profile);
  const isExternal = props.claims.exclusivityStatus.id === ExclusivityStatus.External;

  return (
    <div className="dashboard">
      <h1 className="dashboard__welcome-message">
        Welcome&nbsp;
        {props.claims.firstName}
      </h1>
      {props.isTerminatedAgent && (
        <DashboardCard
          image={() => <i className="fa fa-file-invoice-dollar" />}
          title="Tax Documents"
          description="Your association with this agency has been terminated. You can currently only view your commissions and 1099's for this agency."
          linkText="View Documents"
          onClickLink={() => {
            props.history.push(Ten99RouteConstants.TEN99s);
          }}
        />
      )}
      <RequireScope scopes={props.scopes} scope={Scope.SearchAccounts}>
        <DashboardCard
          image={() => <i className="fa fa-file-invoice" />}
          title="Accounts"
          description="Quickly Search and View Accounts in Your Book of Business."
          linkText="View Accounts"
          onClickLink={() => {
            props.history.push(AccountRouteConstants.ACCOUNTS);
          }}
        />
      </RequireScope>
      <RequireScope scopes={props.scopes} scope={Scope.SearchPolicies}>
        <DashboardCard
          image={() => <i className="fa fa-file-alt" />}
          title="Policies"
          description="Quickly Search and View Policies In Your Book of Business."
          linkText="View Policies"
          onClickLink={() => {
            props.history.push(PolicyRouteConstants.POLICIES);
          }}
        />
      </RequireScope>
      <RequireScope scopes={props.scopes} scope={Scope.SearchCommissions}>
        <DashboardCard
          image={() => <i className="fa fa-money-check-alt" />}
          title="Commissions"
          description="View and Download Earned and Advanced Commission Statements."
          linkText="View Commissions"
          onClickLink={() => {
            props.history.push(CommissionRouteConstants.COMMISSIONS);
          }}
        />
      </RequireScope>
      <RequireScope scopes={props.scopes} scope={Scope.SearchPolicyCorrespondence}>
        <DashboardCard
          image={() => <i className="fa fa-envelope-open-text" />}
          title="Correspondence"
          description="Review and Download Correspondence Related To Your Policies."
          linkText="View Correspondence"
          onClickLink={() => {
            props.history.push(PolicyRouteConstants.CORRESPONDENCE_SUMMARIES());
          }}
        />
      </RequireScope>
      <RequireScope scopes={props.scopes} scope={Scope.SsoAccess}>
        <DashboardCard
          image={() => <i className="fa fa-link" />}
          title="Single Sign-On"
          description="Easily sign in to legacy applications such as EnrollMyWay ."
          linkText="View Legacy Accounts"
          onClickLink={() => {
            props.history.push(UserRouteConstants.SSO_ACCOUNTS);
          }}
        />
      </RequireScope>
      {O.isSome(selectedAgency) && (
        <RequireScope scopes={props.scopes} scope={Scope.ViewAgentNews}>
          <AgentNewsCard
            image={() => <i className="fa fa-newspaper" />}
            linkText="Read Latest News"
            onClickLink={() => {
              props.history.push(AgentInformationRouteConstants.AGENT_NEWS);
            }}
          />
        </RequireScope>
      )}
    </div>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  scopes: selectUserScopes(state),
  isTerminatedAgent: isAgencyInStatus(state, AgentNumberStatusId.Terminated),
});

export default withUserClaims(
  withRouter(
    connect(mapStateToProps, {
      showModal,
    })(Dashboard)
  )
);
